import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  ArrowLeft,
  Calendar,
  User,
  TrendingUp,
  TrendingDown,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../context/SupabaseAuthContext';
import { getPatientHistory } from '../lib/patients';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

export const PatientHistoryPage = () => {
  const { organization, isDemo } = useAuth();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const data = await getPatientHistory(organization.id, patientId);
      setPatient(data.patient);
      setAnalyses(data.analyses);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error(error.message || 'Patient not found');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  }, [navigate, organization.id, patientId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatShortDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return format(date, 'MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const chartData = analyses.map(a => ({
    date: formatShortDate(a.collection_date),
    fullDate: a.collection_date,
    chronological: a.chronological_age,
    pheno: a.pheno_age,
    bortz: a.bortz_age,
    pranagen: a.pranagen_clock,
  }));

  const getLatestTrend = () => {
    if (analyses.length < 2) return null;
    const latest = analyses[analyses.length - 1];
    const previous = analyses[analyses.length - 2];
    
    if (!latest.pranagen_clock || !previous.pranagen_clock) return null;
    
    const diff = latest.pranagen_clock - previous.pranagen_clock;
    return {
      value: Math.abs(diff).toFixed(1),
      direction: diff > 0 ? 'up' : 'down',
      positive: diff < 0,
    };
  };

  const trend = getLatestTrend();

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!patient) return null;

  return (
    <div className="space-y-6" data-testid="patient-history">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {patient.first_name} {patient.last_name}
            </h1>
            <p className="text-slate-600 text-sm font-mono">
              {patient.patient_id_number}
            </p>
          </div>
        </div>
        {!isDemo && (
          <Link to={`/upload?patient=${patientId}`}>
            <Button className="bg-[#196a70] hover:bg-[#12565b] text-white">
              <Upload className="w-4 h-4 mr-2" />
              New Analysis
            </Button>
          </Link>
        )}
      </div>

      {/* Patient Info */}
      <Card className="bg-white border-slate-200 shadow-soft">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                <User className="w-5 h-5 text-[#196a70]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Full Name</p>
                <p className="font-medium text-slate-900">
                  {patient.first_name} {patient.last_name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#196a70]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Date of Birth</p>
                <p className="font-medium text-slate-900">
                  {formatDate(patient.date_of_birth)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                <span className="text-[#196a70] font-semibold">{analyses.length}</span>
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Analyses</p>
                <p className="font-medium text-slate-900">{analyses.length} records</p>
              </div>
            </div>
            {trend && (
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  trend.positive ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {trend.direction === 'up' ? (
                    <TrendingUp className={`w-5 h-5 ${trend.positive ? 'text-green-600' : 'text-red-600'}`} />
                  ) : (
                    <TrendingDown className={`w-5 h-5 ${trend.positive ? 'text-green-600' : 'text-red-600'}`} />
                  )}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Latest Trend</p>
                  <p className={`font-medium ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
                    {trend.direction === 'up' ? '+' : '-'}{trend.value} years
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {analyses.length > 0 ? (
        <>
          {/* Chart */}
          <Card className="bg-white border-slate-200 shadow-soft">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg">
                Biological Age Trend
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      tickLine={{ stroke: '#cbd5e1' }}
                    />
                    <YAxis
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      tickLine={{ stroke: '#cbd5e1' }}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                      labelStyle={{ fontWeight: 600, color: '#0f172a' }}
                      formatter={(value) => (typeof value === 'number' ? value.toFixed(1) : value)}
                    />
                    <Legend />
                    <ReferenceLine
                      y={chartData[0]?.chronological}
                      stroke="#94a3b8"
                      strokeDasharray="5 5"
                      label={{ value: 'Chrono', fill: '#94a3b8', fontSize: 11 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="chronological"
                      stroke="#94a3b8"
                      strokeWidth={2}
                      dot={{ fill: '#94a3b8', strokeWidth: 2 }}
                      name="Chronological"
                    />
                    <Line
                      type="monotone"
                      dataKey="pheno"
                      stroke="#38B2AC"
                      strokeWidth={2}
                      dot={{ fill: '#38B2AC', strokeWidth: 2 }}
                      name="PhenoAge"
                    />
                    <Line
                      type="monotone"
                      dataKey="bortz"
                      stroke="#F59E0B"
                      strokeWidth={2}
                      dot={{ fill: '#F59E0B', strokeWidth: 2 }}
                      name="Bortz Age"
                    />
                    <Line
                      type="monotone"
                      dataKey="pranagen"
                      stroke="#196a70"
                      strokeWidth={3}
                      dot={{ fill: '#196a70', strokeWidth: 2, r: 5 }}
                      name="Pranagen Clock"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* History Table */}
          <Card className="bg-white border-slate-200 shadow-soft">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg">
                Analysis History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {analyses.slice().reverse().map((analysis) => (
                  <Link
                    key={analysis.analysis_id}
                    to={`/analyses/${analysis.analysis_id}`}
                    className="block p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-[#196a70]" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {formatDate(analysis.collection_date)}
                          </p>
                          <p className="text-sm text-slate-500">
                            Chrono: {analysis.chronological_age?.toFixed(1)} years
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold font-heading text-[#196a70]">
                          {analysis.pranagen_clock?.toFixed(1) || '—'}
                        </p>
                        <p className="text-xs text-slate-500">Pranagen Clock</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-heading font-medium text-slate-900 mb-1">
              No analyses yet
            </h3>
            <p className="text-slate-500 text-sm mb-4">
              Upload blood work to start tracking this patient's biological age.
            </p>
            {!isDemo && (
              <Link to={`/upload?patient=${patientId}`}>
                <Button className="bg-[#196a70] hover:bg-[#12565b] text-white">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Blood Work
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
